// src/features/dashboard/context/ManagerDateContext.tsx

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';

import {
  createLocalDate,
  getToday,
  normalizeDate,
  formatApiDate,
} from '../utils/date.utils';

import {
  DashboardDateContext,
  type DashboardDateContextType,
} from './DashboardDateContext';


/* ============================================================
   STORAGE KEYS
============================================================ */

const MANAGER_FROM_DATE_KEY =
  'workvista_manager_from_date';

const MANAGER_TO_DATE_KEY =
  'workvista_manager_to_date';


/* ============================================================
   CONTEXT TYPE
============================================================ */

export type ManagerDateContextType = {
  fromDate: Date;
  toDate: Date;

  setFromDate: (
    date: Date,
  ) => void;

  setToDate: (
    date: Date,
  ) => void;
};


/* ============================================================
   CONTEXT
============================================================ */

export const ManagerDateContext =
  createContext<
    ManagerDateContextType | undefined
  >(undefined);


/* ============================================================
   CREATE LOCAL DATE
============================================================ */

export const createManagerLocalDate = (
  year: number,
  month: number,
  day: number,
): Date => {

  return createLocalDate(
    year,
    month,
    day,
  );
};


/* ============================================================
   NORMALIZE MANAGER DATE
============================================================ */

export const normalizeManagerDate = (
  date: Date,
): Date => {

  return normalizeDate(
    date,
  );
};


/* ============================================================
   FORMAT MANAGER DATE
============================================================ */

export const formatManagerDate = (
  date: Date,
): string => {

  return formatApiDate(
    date,
  );
};


/* ============================================================
   GET MANAGER TODAY
============================================================ */

export const getManagerToday = (): Date => {

  return getToday();
};


/* ============================================================
   READ STORED DATE
     
   IMPORTANT:
   We store YYYY-MM-DD only.
   
   We DO NOT do:
   
       new Date('2026-09-12')
   
   because that can introduce UTC timezone problems.
============================================================ */

const getStoredManagerDate = (
  key: string,
): Date | null => {

  try {

    const storedDate =
      sessionStorage.getItem(
        key,
      );

    if (
      !storedDate
    ) {
      return null;
    }


    const match =
      storedDate.match(
        /^(\d{4})-(\d{2})-(\d{2})$/,
      );

    if (!match) {
      return null;
    }


    const year =
      Number(
        match[1],
      );

    const month =
      Number(
        match[2],
      ) - 1;

    const day =
      Number(
        match[3],
      );


    return createManagerLocalDate(
      year,
      month,
      day,
    );

  } catch (
    error
  ) {

    console.warn(
      'Unable to read Manager date from sessionStorage.',
      error,
    );

    return null;
  }
};


/* ============================================================
   GET INITIAL FROM DATE
============================================================ */

const getInitialFromDate = (): Date => {

  const storedDate =
    getStoredManagerDate(
      MANAGER_FROM_DATE_KEY,
    );

  if (
    storedDate
  ) {
    return storedDate;
  }

  return getManagerToday();
};


/* ============================================================
   GET INITIAL TO DATE
============================================================ */

const getInitialToDate = (): Date => {

  const storedDate =
    getStoredManagerDate(
      MANAGER_TO_DATE_KEY,
    );

  if (
    storedDate
  ) {
    return storedDate;
  }

  return getManagerToday();
};


/* ============================================================
   SAVE DATE
============================================================ */

const saveManagerDate = (
  key: string,
  date: Date,
): void => {

  try {

    const apiDate =
      formatManagerDate(
        date,
      );

    sessionStorage.setItem(
      key,
      apiDate,
    );

  } catch (
    error
  ) {

    console.warn(
      'Unable to save Manager date to sessionStorage.',
      error,
    );
  }
};


/* ============================================================
   PROVIDER
============================================================ */

export const ManagerDateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {

  /* ==========================================================
     INITIAL FROM DATE
     
     First try sessionStorage.
     
     If user previously selected:
     
       12-Sep-2026
     
     navigation will restore:
     
       12-Sep-2026
  ========================================================== */

  const [
    fromDate,
    setFromDateState,
  ] = useState<Date>(
    () => getInitialFromDate(),
  );


  /* ==========================================================
     INITIAL TO DATE
  ========================================================== */

  const [
    toDate,
    setToDateState,
  ] = useState<Date>(
    () => getInitialToDate(),
  );


  /* ==========================================================
     SET FROM DATE
     
     This function:
     
       1. Normalizes date
       2. Updates React state
       3. Saves date
       4. Causes all useManagerDate() hooks to re-run
  ========================================================== */

  const setFromDate =
    useCallback(
      (
        date: Date,
      ) => {

        const normalizedDate =
          normalizeManagerDate(
            date,
          );

        const apiDate =
          formatManagerDate(
            normalizedDate,
          );


        console.log(
          '==========================================',
        );

        console.log(
          'MANAGER FROM DATE CHANGED',
        );

        console.log(
          'Selected Date:',
          normalizedDate,
        );

        console.log(
          'API From Date:',
          apiDate,
        );

        console.log(
          '==========================================',
        );


        /* Update React state */
        setFromDateState(
          normalizedDate,
        );


        /* Persist selected date */
        saveManagerDate(
          MANAGER_FROM_DATE_KEY,
          normalizedDate,
        );
      },
      [],
    );


  /* ==========================================================
     SET TO DATE
  ========================================================== */

  const setToDate =
    useCallback(
      (
        date: Date,
      ) => {

        const normalizedDate =
          normalizeManagerDate(
            date,
          );

        const apiDate =
          formatManagerDate(
            normalizedDate,
          );


        console.log(
          '==========================================',
        );

        console.log(
          'MANAGER TO DATE CHANGED',
        );

        console.log(
          'Selected Date:',
          normalizedDate,
        );

        console.log(
          'API To Date:',
          apiDate,
        );

        console.log(
          '==========================================',
        );


        /* Update React state */
        setToDateState(
          normalizedDate,
        );


        /* Persist selected date */
        saveManagerDate(
          MANAGER_TO_DATE_KEY,
          normalizedDate,
        );
      },
      [],
    );


  /* ==========================================================
     MANAGER CONTEXT VALUE
  ========================================================== */

  const managerValue =
    useMemo(
      () => ({
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      }),
      [
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      ],
    );


  /* ==========================================================
     DASHBOARD CONTEXT BRIDGE
     
     Your existing DashboardHeader uses:
     
       useDashboardDate()
     
     Therefore the SAME manager dates are exposed through
     DashboardDateContext.
     
     There is only ONE actual date state.
  ========================================================== */

  const dashboardValue:
    DashboardDateContextType =
    useMemo(
      () => ({
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      }),
      [
        fromDate,
        toDate,
        setFromDate,
        setToDate,
      ],
    );


  /* ==========================================================
     PROVIDERS
  ========================================================== */

  return (
    <ManagerDateContext.Provider
      value={managerValue}
    >

      <DashboardDateContext.Provider
        value={dashboardValue}
      >

        {children}

      </DashboardDateContext.Provider>

    </ManagerDateContext.Provider>
  );
};


/* ============================================================
   MANAGER DATE HOOK
============================================================ */

export const useManagerDate = () => {

  const context =
    useContext(
      ManagerDateContext,
    );

  if (!context) {

    throw new Error(
      'useManagerDate must be used inside ManagerDateProvider',
    );
  }

  return context;
};